import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, fmtMoney, fmtMoneyCompact, fmtDate } from "@/lib/api";
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar, ResponsiveContainer,
    XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
    TrendingUp, TrendingDown, Wallet, ArrowDownLeft, ArrowUpRight, Receipt,
    FileText, Plus, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InstallAppButton from "@/components/InstallAppButton";

const PIE_COLORS = ["#00A3B5", "#0B2B40", "#FF7F6A", "#1F94A8", "#007A8A", "#54B2C3", "#FF9D8D", "#A0ABC0"];

function Kpi({ label, amount, currency, hint, icon: Icon, tone = "ocean", testId }) {
    const tones = {
        ocean: "from-ocean-50 text-ocean-700",
        secondary: "from-secondary/10 text-secondary-dark",
        coral: "from-coral/10 text-coral-dark",
    };
    const full = fmtMoney(amount || 0, currency);
    const compact = fmtMoneyCompact(amount || 0, currency);
    return (
        <div data-testid={testId} className="rounded-3xl bg-white border border-sand-200 p-4 sm:p-6 shadow-card hover:shadow-card-hover transition-all overflow-hidden">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.18em] text-muted-foreground font-semibold truncate">{label}</div>
                    <div
                        className="mt-2 sm:mt-3 font-heading text-xl sm:text-2xl lg:text-3xl text-ocean-700 font-medium tabular-nums truncate"
                        title={full}
                        data-testid={`${testId}-value`}
                    >
                        {/* Mobile: compact notation (KYD 5.03M). Desktop: full precision. */}
                        <span className="sm:hidden">{compact}</span>
                        <span className="hidden sm:inline">{full}</span>
                    </div>
                </div>
                <div className={`shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br ${tones[tone]} grid place-items-center`}>
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
            </div>
            {hint && <div className="mt-2 sm:mt-3 text-[11px] sm:text-xs text-muted-foreground truncate">{hint}</div>}
        </div>
    );
}

export default function Dashboard() {
    const { user, currentOrg } = useAuth();
    const [data, setData] = useState(null);
    const [range, setRange] = useState(() => localStorage.getItem("bl_dashboard_range") || "ytd");

    useEffect(() => {
        if (!currentOrg) return;
        api.get(`/reports/dashboard?range=${range}`).then(({ data }) => setData(data));
    }, [currentOrg, range]);

    useEffect(() => { localStorage.setItem("bl_dashboard_range", range); }, [range]);

    const currency = currentOrg?.currency || "KYD";
    const label = data?.range_label || range.toUpperCase();
    const rangeHint = {
        mtd: { revenue: "Invoiced this month", expense: "Spend this month" },
        qtd: { revenue: "Invoiced this quarter", expense: "Spend this quarter" },
        ytd: { revenue: "Invoiced this year", expense: "Spend this year" },
        all: { revenue: "All-time invoiced", expense: "All-time spend" },
    }[range] || { revenue: "Invoiced", expense: "Spend" };

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-secondary-dark font-semibold">Dashboard</div>
                    <h1 className="mt-1 font-heading text-4xl text-ocean-700">
                        Good day, {(user?.name || "there").split(" ")[0]}.
                    </h1>
                    <p className="mt-1 text-muted-foreground">Here's how <span className="text-ocean-700 font-medium">{currentOrg?.name}</span> is moving.</p>
                </div>
                <div className="flex gap-2 items-center">
                    <Select value={range} onValueChange={setRange}>
                        <SelectTrigger className="w-[150px] rounded-xl border-sand-300 bg-white" data-testid="dashboard-range">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="mtd">This month</SelectItem>
                            <SelectItem value="qtd">This quarter</SelectItem>
                            <SelectItem value="ytd">Year to date</SelectItem>
                            <SelectItem value="all">All time</SelectItem>
                        </SelectContent>
                    </Select>
                    <Link to="/app/invoices"><Button className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" data-testid="dashboard-new-invoice"><Plus className="h-4 w-4 mr-1.5" /> New invoice</Button></Link>
                    <Link to="/app/expenses"><Button variant="outline" className="rounded-xl border-sand-300" data-testid="dashboard-new-expense"><Receipt className="h-4 w-4 mr-1.5" /> Log expense</Button></Link>
                    <InstallAppButton />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <Kpi testId="kpi-cash" label="Cash on hand" amount={data?.kpi.cash_balance || 0} currency={currency} hint="Across all accounts" icon={Wallet} tone="ocean" />
                <Kpi testId="kpi-revenue" label={`Revenue (${label})`} amount={data?.kpi.revenue_mtd || 0} currency={currency} hint={rangeHint.revenue} icon={ArrowDownLeft} tone="secondary" />
                <Kpi testId="kpi-expense" label={`Expenses (${label})`} amount={data?.kpi.expenses_mtd || 0} currency={currency} hint={rangeHint.expense} icon={ArrowUpRight} tone="coral" />
                <Kpi testId="kpi-net" label={`Net (${label})`} amount={data?.kpi.net_mtd || 0} currency={currency} hint={(data?.kpi.net_mtd || 0) >= 0 ? "Profitable" : "In the red"} icon={(data?.kpi.net_mtd || 0) >= 0 ? TrendingUp : TrendingDown} tone="secondary" />
                <Kpi testId="kpi-ar" label="AR outstanding" amount={data?.kpi.ar_outstanding || 0} currency={currency} hint="Owed to you" icon={FileText} tone="ocean" />
                <Kpi testId="kpi-ap" label="AP outstanding" amount={data?.kpi.ap_outstanding || 0} currency={currency} hint="You owe" icon={FileText} tone="coral" />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-3xl bg-white border border-sand-200 p-6 shadow-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-heading text-xl text-ocean-700">Cash flow · Last 6 months</h3>
                            <p className="text-xs text-muted-foreground mt-1">Income vs. expenses</p>
                        </div>
                        <Badge className="bg-ocean-50 text-ocean-700 border-0">{currency}</Badge>
                    </div>
                    <div className="h-72 mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data?.cashflow || []} margin={{ left: -10 }}>
                                <defs>
                                    <linearGradient id="incFill" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0" stopColor="#00A3B5" stopOpacity="0.35" />
                                        <stop offset="1" stopColor="#00A3B5" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="expFill" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0" stopColor="#FF7F6A" stopOpacity="0.3" />
                                        <stop offset="1" stopColor="#FF7F6A" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#E2E6EF" vertical={false} />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#718096" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#718096" }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E6EF" }} />
                                <Area dataKey="income" stroke="#00A3B5" strokeWidth={2.2} fill="url(#incFill)" name="Income" />
                                <Area dataKey="expense" stroke="#FF7F6A" strokeWidth={2.2} fill="url(#expFill)" name="Expense" />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="rounded-3xl bg-white border border-sand-200 p-6 shadow-card">
                    <h3 className="font-heading text-xl text-ocean-700">Expense mix</h3>
                    <p className="text-xs text-muted-foreground mt-1">Where the money goes</p>
                    {(() => {
                        const cats = data?.expense_categories || [];
                        const total = cats.reduce((s, c) => s + (c.amount || 0), 0) || 1;
                        return (
                            <>
                                <div className="h-72 mt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={cats} dataKey="amount" nameKey="category" innerRadius={50} outerRadius={90} paddingAngle={2}>
                                                {cats.map((c, i) => (
                                                    <Cell key={c.category || `cat-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{ borderRadius: 12, border: "1px solid #E2E6EF" }}
                                                formatter={(value, name) => {
                                                    const pct = ((value / total) * 100).toFixed(1);
                                                    return [`${fmtMoney(value, currency)} (${pct}%)`, name];
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="space-y-1.5" data-testid="expense-mix-legend">
                                    {cats.slice(0, 5).map((c, i) => {
                                        const pct = ((c.amount / total) * 100).toFixed(1);
                                        return (
                                            <div key={c.category || `legend-${i}`} className="flex items-center justify-between text-xs">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                                                    <span className="text-ocean-700 truncate">{c.category}</span>
                                                </div>
                                                <div className="flex items-center gap-2 font-mono text-muted-foreground whitespace-nowrap">
                                                    <span className="text-[10px] text-secondary-dark font-semibold tabular-nums">{pct}%</span>
                                                    <span>{fmtMoney(c.amount, currency)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-3xl bg-white border border-sand-200 p-6 shadow-card">
                    <div className="flex items-center justify-between">
                        <h3 className="font-heading text-xl text-ocean-700">Recent invoices</h3>
                        <Link to="/app/invoices" className="text-xs text-secondary-dark hover:text-secondary">View all →</Link>
                    </div>
                    <div className="mt-4 divide-y divide-sand-200">
                        {(data?.recent_invoices || []).map((r) => (
                            <div key={r.id} className="flex items-center justify-between py-3">
                                <div>
                                    <div className="text-sm font-medium text-ocean-700">{r.customer_name}</div>
                                    <div className="text-xs text-muted-foreground">{r.number} · {fmtDate(r.issue_date)}</div>
                                </div>
                                <div className="font-mono text-sm">{fmtMoney(r.total, r.currency)}</div>
                                <Badge className={`border-0 ${
                                    r.status === "paid" ? "bg-emerald-50 text-emerald-700"
                                  : r.status === "overdue" ? "bg-coral/10 text-coral-dark"
                                  : r.status === "partial" ? "bg-amber-50 text-amber-700"
                                  : r.status === "draft" ? "bg-sand-200 text-muted-foreground"
                                  : "bg-secondary/10 text-secondary-dark"
                                }`}>{r.status}</Badge>
                            </div>
                        ))}
                        {!data?.recent_invoices?.length && (
                            <div className="py-10 text-center text-sm text-muted-foreground">No invoices yet — create your first one to get started.</div>
                        )}
                    </div>
                </div>

                <div className="rounded-3xl text-white p-6 shadow-card bg-deep-ocean relative overflow-hidden">
                    <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-secondary/30 blur-3xl" />
                    <Sparkles className="h-6 w-6 text-secondary-light" />
                    <h3 className="mt-4 font-heading text-2xl">Cayman tax mode is on</h3>
                    <p className="mt-2 text-white/70 text-sm">All invoices and reports default to zero corporate tax. Override per line item if you ever need to.</p>
                    <Link to="/app/settings" className="mt-6 inline-flex items-center text-secondary-light text-sm font-medium hover:text-white transition-colors">
                        Manage tax settings →
                    </Link>
                </div>
            </div>
        </div>
    );
}
